/**
 * @fileoverview CAPTCHA Configuration
 * @module @puffin-core/captcha-ui/captchaConfig
 * @description Centralized configuration for the CaptchaField component.
 *
 * Encapsulates all `import.meta.env` references so consumers (and Jest) can
 * read a stable API surface. In production, `VITE_TURNSTILE_SITE_KEY` MUST
 * be configured — the component renders an error state otherwise. In
 * development, forms can work without CAPTCHA for testing purposes (a
 * `DEV · CAPTCHA bypassed` warning is shown).
 *
 * This module is Vite-specific (reads `import.meta.env.VITE_*`). All nine
 * Colony tenant web apps are Vite apps today; if a non-Vite consumer is
 * added in the future, this module must be adapted.
 */

/**
 * Cloudflare Turnstile site key from environment configuration.
 * @constant {string|undefined}
 */
export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

/**
 * Whether the application is running in development mode.
 * @constant {boolean}
 */
export const IS_DEVELOPMENT = import.meta.env.DEV === true;

/**
 * Whether CAPTCHA is properly configured with a site key.
 * @constant {boolean}
 */
export const IS_CAPTCHA_CONFIGURED = Boolean(TURNSTILE_SITE_KEY);

/**
 * Pure derivation of the dev-warning flag.
 *
 * Extracted as a standalone function so both arms of the boolean logic are
 * directly exercisable in tests. Under Vite, `import.meta.env.DEV` is a real
 * boolean and the module-level `IS_DEVELOPMENT` constant can be either value;
 * under Jest's `import.meta.env → process.env` babel rewrite, env values are
 * always strings, so the module-load value of `IS_DEVELOPMENT` is pinned to
 * `false` and the `&&` right arm would never be reached if it lived inline.
 * Routing the constant through this pure helper keeps production behaviour
 * byte-identical while making the truth table testable.
 *
 * @param {boolean} isDevelopment Whether the app is in dev mode.
 * @param {boolean} isCaptchaConfigured Whether a Turnstile site key is set.
 * @returns {boolean} True only when in dev mode AND no site key is configured.
 */
export const deriveShowDevWarning = (isDevelopment, isCaptchaConfigured) =>
  isDevelopment && !isCaptchaConfigured;

/**
 * Whether to show a development warning about CAPTCHA bypass.
 * True only when the app is in dev mode AND the site key is absent.
 * @constant {boolean}
 */
export const SHOW_DEV_WARNING = deriveShowDevWarning(IS_DEVELOPMENT, IS_CAPTCHA_CONFIGURED);

/**
 * Pure derivation of the app-level "CAPTCHA required for form submission"
 * policy flag: required in production always; required in development only
 * when a site key is configured (so local devs without a key can still
 * submit forms).
 *
 * Promoted here (CLEAN-SWEEP-001 follow-up Item 3, 2026-07-11) from six
 * byte-identical app-local derivations (`IS_DEVELOPMENT ? IS_CAPTCHA_CONFIGURED
 * : true` in pos/palm/patrol/payday/prospect/purchase-web `turnstileConfig.js`).
 * Same rationale as `deriveShowDevWarning` above: under Jest's
 * `import.meta.env → process.env` babel rewrite the module-load value of
 * `IS_DEVELOPMENT` is pinned, so the production arm of the ternary would be
 * unreachable if it lived inline in a consumer module — routing it through
 * this pure helper keeps production behaviour byte-identical while making
 * the truth table testable. Consumers derive their own module constant:
 * `export const IS_CAPTCHA_REQUIRED = deriveCaptchaRequired(IS_DEVELOPMENT,
 * IS_CAPTCHA_CONFIGURED);` — this package deliberately does NOT export an
 * `IS_CAPTCHA_REQUIRED` constant because requiredness is app policy.
 *
 * @param {boolean} isDevelopment Whether the app is in dev mode.
 * @param {boolean} isCaptchaConfigured Whether a Turnstile site key is set.
 * @returns {boolean} True when CAPTCHA must be completed before submission.
 */
export const deriveCaptchaRequired = (isDevelopment, isCaptchaConfigured) =>
  isDevelopment ? isCaptchaConfigured : true;

/**
 * Get configuration object for CAPTCHA. Returns a fresh object on each call.
 * @returns {{siteKey: string|undefined, isDevelopment: boolean, isCaptchaConfigured: boolean, showDevWarning: boolean}}
 */
export const getCaptchaConfig = () => ({
  siteKey: TURNSTILE_SITE_KEY,
  isDevelopment: IS_DEVELOPMENT,
  isCaptchaConfigured: IS_CAPTCHA_CONFIGURED,
  showDevWarning: SHOW_DEV_WARNING,
});
